import BaseController from "./base_controller"
import { t } from "@/utils/i18n"
import { toDateInputValue } from "@/utils/dates"

// Connects to data-controller="statistics"
export default class extends BaseController {
  static targets = [
    "periodSelector", 
    "customInline", 
    "customMobile", 
    "startDate", 
    "endDate",
    "mobileStartDate",
    "mobileEndDate"
  ]
  
  declare readonly periodSelectorTarget: HTMLElement
  declare readonly customInlineTarget: HTMLElement
  declare readonly customMobileTarget: HTMLElement
  declare readonly startDateTarget: HTMLInputElement
  declare readonly endDateTarget: HTMLInputElement
  declare readonly mobileStartDateTarget: HTMLInputElement
  declare readonly mobileEndDateTarget: HTMLInputElement
  declare readonly hasPeriodSelectorTarget: boolean
  declare readonly hasCustomInlineTarget: boolean
  declare readonly hasCustomMobileTarget: boolean
  declare readonly hasStartDateTarget: boolean
  declare readonly hasEndDateTarget: boolean
  declare readonly hasMobileStartDateTarget: boolean
  declare readonly hasMobileEndDateTarget: boolean
  
  private isNavigating: boolean = false
  private customRangeVisible: boolean = false
  private resizeHandler: (() => void) | null = null
  private turboLoadHandler: ((event: Event) => void) | null = null

  connect(): void {
    super.connect()
    this.isNavigating = false // Prevent concurrent navigation
    this.customRangeVisible = false // Track custom range state
    this.syncActiveTabOnLoad()
    this.restoreCustomInputsIfActive() // Rails-native persistence support
    this.setupTurboEventListeners()
    this.setupResponsiveListeners()
  }

  disconnect(): void {
    super.disconnect()
    this.cleanupTurboEventListeners()
    this.cleanupResponsiveListeners()
  }

  // Toggle custom range (new inline method)
  toggleCustomRange(event: Event): void {
    try {
      event.preventDefault()
      
      // Update active tab
      this.updateCustomTabActive()
      
      if (this.customRangeVisible) {
        this.hideCustomRange()
      } else {
        this.showCustomRange()
      }
      
    } catch (error) {
      this.handleError(error, 'toggleCustomRange')
    }
  }

  // Show custom range (responsive) - CSS Transitions Only
  showCustomRange() {
    try {
      this.customRangeVisible = true
      
      // Show appropriate custom range based on screen size
      // CSS transitions handle the animation automatically
      if (this.isDesktop()) {
        this.showInlineCustomRange()
      } else {
        this.showMobileCustomRange()
      }
      
      // Initialize date inputs
      this.initializeDateInputs()
      
    } catch (error) {
      this.handleError(error, 'showCustomRange')
    }
  }

  // Hide custom range (responsive) - CSS Transitions Only
  hideCustomRange() {
    try {
      this.customRangeVisible = false
      
      // Hide both inline and mobile ranges
      // CSS transitions handle the animation automatically
      if (this.hasCustomInlineTarget) {
        this.customInlineTarget.classList.remove('show')
        this.customInlineTarget.classList.add('hidden')
      }
      
      if (this.hasCustomMobileTarget) {
        this.customMobileTarget.classList.remove('show')
        this.customMobileTarget.classList.add('hidden')
      }
      
    } catch (error) {
      this.handleError(error, 'hideCustomRange')
    }
  }

  // Show inline custom range (desktop)
  showInlineCustomRange() {
    if (this.hasCustomInlineTarget) {
      this.customInlineTarget.classList.remove('hidden')
      this.customInlineTarget.classList.add('show')
    }
    
    // Hide mobile version
    if (this.hasCustomMobileTarget) {
      this.customMobileTarget.classList.remove('show')
      this.customMobileTarget.classList.add('hidden')
    }
  }

  // Show mobile custom range (mobile/tablet)
  showMobileCustomRange() {
    if (this.hasCustomMobileTarget) {
      this.customMobileTarget.classList.remove('hidden')
      this.customMobileTarget.classList.add('show')
    }
    
    // Hide inline version
    if (this.hasCustomInlineTarget) {
      this.customInlineTarget.classList.remove('show')
      this.customInlineTarget.classList.add('hidden')
    }
  }

  // Rails form submissions now handle the navigation
  // These methods are kept for progressive enhancement and validation
  
  // Restore custom inputs if server indicates custom range is active
  restoreCustomInputsIfActive() {
    try {
      // Check if custom period is active (from server-side)
      if (this.hasCustomActiveClass()) {
        this.customRangeVisible = true
        this.showCustomRange()
      }
    } catch (error) {
      this.handleError(error, 'restoreCustomInputsIfActive')
    }
  }

  // Check if custom tab has active class (server-side determined)
  hasCustomActiveClass() {
    // Find custom tab div with min-width class and check if it has tab-active
    const customTab = document.querySelector('.tab.min-w-\\[200px\\].tab-active')
    return customTab !== null
  }

  // Cancel custom range
  cancelCustomRange(event: Event): void {
    try {
      event.preventDefault()
      this.hideCustomRange()
      
      // Reset to default period (30 days)
      const url = `/statistics?period=30`
      this.navigateToStatistics(url)
      
    } catch (error) {
      this.handleError(error, 'cancelCustomRange')
    }
  }

  // Validate custom range inputs
  validateCustomRange(event: Event): void {
    const input = event.currentTarget as HTMLElement
    const startDate = this.startDateTarget.value
    const endDate = this.endDateTarget.value
    
    this.validateRangeInputs(startDate, endDate, input)
  }

  // Validate mobile custom range inputs
  validateMobileCustomRange(event: Event): void {
    const input = event.currentTarget as HTMLElement
    const startDate = this.mobileStartDateTarget.value
    const endDate = this.mobileEndDateTarget.value
    
    this.validateRangeInputs(startDate, endDate, input)
  }

  private validateRangeInputs(startDate: string, endDate: string, input: EventTarget | HTMLElement): void {
    // Real-time validation feedback
    const inputElement = input as HTMLElement
    if (startDate && endDate) {
      if (new Date(startDate) > new Date(endDate)) {
        inputElement.classList.add('input-error')
        this.showAlert(t('date_from_must_be_before'), 'warning')
      } else {
        inputElement.classList.remove('input-error')
      }
    }
  }

  // Update custom tab active state - FIXED SELECTORS
  updateCustomTabActive() {
    // Remove active from all standard period tabs (actual template selectors)
    this.element.querySelectorAll('.period-tabs a.tab').forEach(tab => {
      tab.classList.remove('tab-active', 'text-primary', 'font-bold', 'border-primary')
      tab.classList.add('hover:text-primary', 'transition-colors')
    })
    
    // Add active to custom tab (actual template selector)
    const customTab = this.element.querySelector('.period-tabs div.tab.min-w-\\[200px\\]')
    if (customTab) {
      customTab.classList.add('tab-active', 'text-primary', 'font-bold', 'border-primary')
      customTab.classList.remove('hover:text-primary')
    }
  }

  // Initialize date inputs with defaults
  initializeDateInputs() {
    const today = new Date()
    const thirtyDaysAgo = new Date(today.getTime() - (30 * 24 * 60 * 60 * 1000))
    
    // Format dates for input (local calendar day, not UTC)
    const todayStr = toDateInputValue(today)
    const thirtyDaysAgoStr = toDateInputValue(thirtyDaysAgo)
    
    // Set desktop inputs
    if (this.hasStartDateTarget && !this.startDateTarget.value) {
      this.startDateTarget.value = thirtyDaysAgoStr
    }
    if (this.hasEndDateTarget && !this.endDateTarget.value) {
      this.endDateTarget.value = todayStr
    }
    
    // Set mobile inputs
    if (this.hasMobileStartDateTarget && !this.mobileStartDateTarget.value) {
      this.mobileStartDateTarget.value = thirtyDaysAgoStr
    }
    if (this.hasMobileEndDateTarget && !this.mobileEndDateTarget.value) {
      this.mobileEndDateTarget.value = todayStr
    }
  }

  // Responsive detection
  isDesktop() {
    return window.innerWidth >= 1024
  }

  // Setup responsive listeners
  setupResponsiveListeners() {
    this.resizeHandler = this.handleResize.bind(this)
    window.addEventListener('resize', this.resizeHandler)
  }

  cleanupResponsiveListeners() {
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler)
    }
  }

  // Handle window resize
  handleResize() {
    if (this.customRangeVisible) {
      // Switch between inline and mobile display
      if (this.isDesktop()) {
        this.showInlineCustomRange()
      } else {
        this.showMobileCustomRange()
      }
    }
  }

  // Rest of existing methods remain the same...
  private navigateToStatistics(url: string): void {
    try {
      // Prevent concurrent navigation
      if (this.isNavigating) {
        return
      }
      
      this.isNavigating = true
      
      // Show loading state
      this.showLoading()
      
      // Use Turbo.visit for full page navigation with error handling
      Turbo.visit(url, { action: "replace" })
      
      // Reset navigation flag after timeout (fallback)
      setTimeout(() => {
        this.isNavigating = false
        this.hideLoading()
      }, 3000)
      
    } catch (error) {
      this.handleError(error, 'navigateToStatistics')
      this.isNavigating = false
      this.hideLoading()
    }
  }

  private updateActivePeriodTab(activeTab: HTMLElement): void {
    // Remove active state from all tabs
    this.element.querySelectorAll<HTMLElement>('.tab').forEach((tab: HTMLElement) => {
      tab.classList.remove('tab-active', 'text-primary', 'font-bold', 'border-primary')
    })
    
    // Add active state to clicked tab
    activeTab.classList.add('tab-active', 'text-primary', 'font-bold', 'border-primary')
  }

  syncActiveTabOnLoad() {
    try {
      if (!this.hasPeriodSelectorTarget) {
        return
      }

      const currentPeriod = this.periodSelectorTarget?.dataset?.currentPeriod

      if (currentPeriod) {
        const activeTab = this.element.querySelector<HTMLElement>(`[data-period="${currentPeriod}"]`)
        if (activeTab) {
          this.updateActivePeriodTab(activeTab)
        }
      } else {
        // Custom period is active
        this.updateCustomTabActive()
      }
      
    } catch (error) {
      this.handleError(error, 'syncActiveTabOnLoad')
    }
  }

  setupTurboEventListeners() {
    this.turboLoadHandler = this.handleTurboLoad.bind(this)
    document.addEventListener('turbo:load', this.turboLoadHandler)
  }

  cleanupTurboEventListeners() {
    if (this.turboLoadHandler) {
      document.removeEventListener('turbo:load', this.turboLoadHandler)
    }
  }

  handleTurboLoad() {
    this.isNavigating = false
    this.hideLoading()
    this.syncActiveTabOnLoad()
  }

  showLoading() {
    // Add loading state to page
    const loadingIndicator = this.element.querySelector('.loading-indicator')
    if (loadingIndicator) {
      loadingIndicator.classList.remove('hidden')
    }
  }

  hideLoading() {
    // Remove loading state from page
    const loadingIndicator = this.element.querySelector('.loading-indicator')
    if (loadingIndicator) {
      loadingIndicator.classList.add('hidden')
    }
  }
} 